/* eslint-disable no-console */
/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable indent */
/* eslint-disable quotes */
/**
 * MarketplaceContext - Script Marketplace state management and API functions
 * 
 * Provides marketplace functionality for browsing, buying, and selling scripts.
 * Access is restricted to Team Leads and Admins only.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  limit,
  startAfter,
  onSnapshot, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  doc, 
  getDoc, 
  getDocs,
  getDocsFromServer,
  increment,
  serverTimestamp
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions, getArtifactProjectId } from '../firebaseInit';
import { useAuth } from './AuthContext';

// Marketplace categories configuration
export const MARKETPLACE_CATEGORIES = [
  { id: 'real_estate', name: 'Real Estate', icon: '🏠' },
  { id: 'insurance', name: 'Insurance', icon: '🛡️' },
  { id: 'solar', name: 'Solar & Energy', icon: '☀️' },
  { id: 'b2b_sales', name: 'B2B Sales', icon: '💼' },
  { id: 'financial', name: 'Financial Services', icon: '🏦' },
  { id: 'home_services', name: 'Home Services', icon: '🔧' },
  { id: 'healthcare', name: 'Healthcare', icon: '🏥' },
  { id: 'telecom', name: 'Telecom', icon: '📱' },
  { id: 'other', name: 'Other', icon: '📋' }
];

// Report reasons
export const REPORT_REASONS = [
  { id: 'misleading', name: 'Misleading Content' },
  { id: 'low_quality', name: 'Low Quality' },
  { id: 'inappropriate', name: 'Inappropriate Content' },
  { id: 'spam', name: 'Spam' },
  { id: 'other', name: 'Other' }
];

// Listing statuses
export const LISTING_STATUS = {
  DRAFT: 'draft',
  PENDING_REVIEW: 'pending_review',
  ACTIVE: 'active',
  SUSPENDED: 'suspended',
  REJECTED: 'rejected',
  DELETED: 'deleted'
};

// Pricing rules
export const PRICING_RULES = {
  MIN_PRICE: 10,  // Minimum $10 for paid scripts
  MAX_PRICE: null, // No maximum
  SELLER_COMMISSION: 0.80, // Sellers keep 80%
  PLATFORM_FEE: 0.20 // TreeGenie takes 20%
};

// Automated validation checks
const VALIDATION_CHECKS = {
  MIN_DESCRIPTION_LENGTH: 100,
  MIN_NODE_COUNT: 3,
  PROFANITY_WORDS: ['spam', 'scam', 'fake'] // Basic list, expand as needed
};

// Debug mode for marketplace
const DEBUG_MARKETPLACE = process.env.REACT_APP_DEBUG_MARKETPLACE === '1' || process.env.NODE_ENV === 'development';

const debugLog = (message, ...args) => {
  if (DEBUG_MARKETPLACE) {
    console.log(`🛒 [MARKETPLACE] ${message}`, ...args);
  }
};


/**
 * Log Firebase errors with full details for debugging
 */
const logFirebaseError = (operation, error) => {
  // Only log if we have a real error (not just missing data)
  if (!error) return;
  
  const errorDetails = {
    code: error?.code || 'no-code',
    message: error?.message || 'no-message',
    name: error?.name || 'no-name'
  };
  
  // Only log in development or if it's a real error (not permission-denied which is common)
  if (process.env.NODE_ENV === 'development') {
    console.error(`🛒 [MARKETPLACE ERROR] ${operation}:`, JSON.stringify(errorDetails, null, 2));
  } else if (error?.code !== 'permission-denied') {
    console.error(`🛒 [MARKETPLACE ERROR] ${operation}:`, errorDetails.message);
  }
};

/**
 * Convert Firebase errors to user-friendly messages
 */
const getErrorMessage = (error) => {
  const code = error?.code || '';
  const message = error?.message || '';
  
  // Firebase error codes
  if (code === 'permission-denied' || message.includes('permission')) {
    return 'You don\'t have permission to access this. Please try logging out and back in.';
  }
  if (code === 'failed-precondition' || message.includes('index')) {
    return 'The marketplace is being set up. Please try again in a few minutes.';
  }
  if (code === 'not-found') {
    return 'The item you\'re looking for couldn\'t be found.';
  }
  if (code === 'unavailable' || message.includes('offline')) {
    return 'Connection lost. Please check your internet and try again.';
  }
  if (code === 'resource-exhausted') {
    return 'Too many requests. Please wait a moment and try again.';
  }
  
  // Generic fallback
  return 'Something went wrong. Please try again.';
};

const MarketplaceContext = createContext();

export const useMarketplace = () => {
  const context = useContext(MarketplaceContext);
  if (!context) {
    throw new Error('useMarketplace must be used within a MarketplaceProvider');
  }
  return context;
};

export const MarketplaceProvider = ({ children }) => {
  const { user, userRole, userTeam, userTeamName, userEmail, isAdmin, isTeamLead, effectiveTeamId, isViewingOtherTeam, viewingTeamId } = useAuth();
  
  // Use effectiveTeamId for admins viewing other teams, otherwise use userTeam
  const activeTeamId = effectiveTeamId || userTeam;
  
  // State
  const [listings, setListings] = useState([]);
  const [featuredListings, setFeaturedListings] = useState([]);
  const [myListings, setMyListings] = useState([]);
  const [myPurchases, setMyPurchases] = useState([]);
  const [pendingReviewListings, setPendingReviewListings] = useState([]); // Admin only
  const [reviewedListings, setReviewedListings] = useState([]); // Admin only: approved + rejected
  const [loading, setLoading] = useState(false); // Start false - lazy loading
  const [error, setError] = useState(null);
  
  // Separate loading states for each page type (for more granular UI feedback)
  // All start as true so pages show loading until initializeMarketplace is called
  const [listingsLoading, setListingsLoading] = useState(true);
  const [purchasesLoading, setPurchasesLoading] = useState(true);
  const [myListingsLoading, setMyListingsLoading] = useState(true);
  
  // Pagination state
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  
  // Role-based access check - only Team Leads and Admins can access marketplace
  const hasMarketplaceAccess = isAdmin || isTeamLead;
  
  // Track if initial load has completed (to differentiate "loading" from "empty")
  const initialLoadAttempted = useRef(false);
  
  // Get Firestore paths - MEMOIZE to prevent infinite re-renders
  const appId = useMemo(() => getArtifactProjectId(), []);
  const marketplacePath = useMemo(() => `artifacts/${appId}/marketplace`, [appId]);
  const purchasesPath = useMemo(() => `artifacts/${appId}/marketplace_purchases`, [appId]);
  const reviewsPath = useMemo(() => `artifacts/${appId}/marketplace_reviews`, [appId]);
  const reportsPath = useMemo(() => `artifacts/${appId}/marketplace_reports`, [appId]);

  // Debug: Log marketplace configuration on init
  debugLog('Marketplace initialized', {
    hasMarketplaceAccess,
    isAdmin,
    isTeamLead,
    userRole,
    userId: user?.uid,
    userEmail,
    appId,
    marketplacePath
  });

  // Validate listing data before publish
  const validateListing = useCallback((listingData, template) => {
    const errors = [];
    
    // Description length check
    if (!listingData.description || listingData.description.length < VALIDATION_CHECKS.MIN_DESCRIPTION_LENGTH) {
      errors.push({
        check: 'description_length',
        message: `Please add more detail to your description (minimum ${VALIDATION_CHECKS.MIN_DESCRIPTION_LENGTH} characters)`
      });
    }
    
    // Node count check — handle both {nodes: {...}} and flat graph formats
    const rawGraph = template?.graph;
    const graphNodesForValidation = rawGraph?.nodes && typeof rawGraph.nodes === 'object'
      ? rawGraph.nodes
      : rawGraph;
    const nodeCount = graphNodesForValidation
      ? Object.keys(graphNodesForValidation).filter(k => k !== 'ui' && k !== 'nodes').length
      : 0;
    if (nodeCount < VALIDATION_CHECKS.MIN_NODE_COUNT) {
      errors.push({
        check: 'node_count',
        message: `Script must have at least ${VALIDATION_CHECKS.MIN_NODE_COUNT} conversation nodes`
      });
    }
    
    // Profanity check
    const lowerDesc = (listingData.description || '').toLowerCase();
    const lowerTitle = (listingData.title || '').toLowerCase();
    const hasProfanity = VALIDATION_CHECKS.PROFANITY_WORDS.some(
      word => lowerDesc.includes(word) || lowerTitle.includes(word)
    );
    if (hasProfanity) {
      errors.push({
        check: 'profanity',
        message: 'Description contains inappropriate content'
      });
    }
    
    // Price validation
    if (!listingData.isFree && listingData.price < PRICING_RULES.MIN_PRICE) {
      errors.push({
        check: 'price',
        message: `Minimum price for paid scripts is $${PRICING_RULES.MIN_PRICE}`
      });
    }
    
    return errors;
  }, []);

  // Load active listings (public marketplace)
  const loadListings = useCallback(async (filters = {}, sortBy = 'popular', pageSize = 20, loadMore = false) => {
    // Mark that we've attempted initial load
    initialLoadAttempted.current = true;
    
    if (!hasMarketplaceAccess) {
      setLoading(false);
      setListingsLoading(false);
      setListings([]); // Ensure empty array for proper UI state
      return;
    }
    
    try {
      if (!loadMore) {
        setLoading(true);
        setListingsLoading(true);
        setError(null);
      }
      
      const marketplaceRef = collection(db, marketplacePath);
      let q = query(
        marketplaceRef,
        where('status', '==', LISTING_STATUS.ACTIVE)
      );
      
      // Apply category filter
      if (filters.category && filters.category !== 'all') {
        q = query(q, where('category', '==', filters.category));
      }
      
      // Apply free/paid filter
      if (filters.priceType === 'free') {
        q = query(q, where('isFree', '==', true));
      } else if (filters.priceType === 'paid') {
        q = query(q, where('isFree', '==', false));
      }
      
      // Apply sorting
      switch (sortBy) {
        case 'newest':
          q = query(q, orderBy('publishedAt', 'desc'));
          break;
        case 'price_low':
          q = query(q, orderBy('price', 'asc'));
          break;
        case 'price_high':
          q = query(q, orderBy('price', 'desc'));
          break;
        case 'rating':
          q = query(q, orderBy('rating', 'desc'));
          break;
        case 'popular':
        default:
          q = query(q, orderBy('totalSales', 'desc'));
          break;
      }
      
      // Pagination
      q = query(q, limit(pageSize));
      if (loadMore && lastVisible) {
        q = query(q, startAfter(lastVisible));
      }
      
      const snapshot = await getDocs(q);
      const newListings = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Update pagination state
      if (snapshot.docs.length > 0) {
        setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
      }
      setHasMore(snapshot.docs.length === pageSize);
      
      if (loadMore) {
        setListings(prev => [...prev, ...newListings]);
      } else {
        setListings(newListings);
        setLastVisible(null);
      }
      
      setError(null);
    } catch (err) {
      // If index not ready, retry without sorting
      if (err?.code === 'failed-precondition' || err?.message?.includes('index')) {
        console.warn('Marketplace index not ready, fetching without sort order...');
        try {
          const marketplaceRef = collection(db, marketplacePath);
          let fallbackQuery = query(
            marketplaceRef,
            where('status', '==', LISTING_STATUS.ACTIVE),
            limit(pageSize)
          );
          
          const fallbackSnapshot = await getDocs(fallbackQuery);
          const fallbackListings = fallbackSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          
          setListings(fallbackListings);
          setHasMore(fallbackSnapshot.docs.length === pageSize);
          setError(null);
          setLoading(false);
          setListingsLoading(false);
          return;
        } catch (fallbackErr) {
          console.error('Fallback query also failed:', fallbackErr);
        }
      }
      
      logFirebaseError('loadListings', err);
      const friendlyError = getErrorMessage(err);
      setError(friendlyError);
      // Set empty listings on error so UI shows empty state
      if (!loadMore) {
        setListings([]);
      }
    } finally {
      setLoading(false);
      setListingsLoading(false);
    }
  }, [hasMarketplaceAccess, marketplacePath, lastVisible]);

  // Load featured listings
  const loadFeaturedListings = useCallback(async () => {
    if (!hasMarketplaceAccess) {
      setFeaturedListings([]);
      return;
    }
    
    try {
      const marketplaceRef = collection(db, marketplacePath);
      const q = query(
        marketplaceRef,
        where('featured', '==', true),
        where('status', '==', LISTING_STATUS.ACTIVE),
        orderBy('totalSales', 'desc'),
        limit(6)
      );
      
      const snapshot = await getDocs(q);
      setFeaturedListings(snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })));
    } catch (err) {
      // If index not ready, retry without sorting
      if (err?.code === 'failed-precondition' || err?.message?.includes('index')) {
        console.warn('FeaturedListings index not ready, fetching without sort...');
        try {
          const marketplaceRef = collection(db, marketplacePath);
          const fallbackQuery = query(
            marketplaceRef,
            where('featured', '==', true),
            limit(6)
          );
          
          const fallbackSnapshot = await getDocs(fallbackQuery);
          // Filter for active status client-side
          const featured = fallbackSnapshot.docs
            .map(doc => ({ id: doc.id, ...doc.data() }))
            .filter(l => l.status === LISTING_STATUS.ACTIVE);
          
          setFeaturedListings(featured);
          return;
        } catch (fallbackErr) {
          console.error('Fallback query also failed:', fallbackErr);
        }
      }
      
      logFirebaseError('loadFeaturedListings', err);
      // Featured listings are optional, don't block the page
      setFeaturedListings([]);
    }
  }, [hasMarketplaceAccess, marketplacePath]);

  // Load user's own listings (for sellers) - or team's listings when admin views another team
  const loadMyListings = useCallback(async () => {
    if (!hasMarketplaceAccess || !user?.uid) {
      setMyListings([]);
      setMyListingsLoading(false);
      return;
    }
    
    setMyListingsLoading(true);
    debugLog('loadMyListings - isViewingOtherTeam:', isViewingOtherTeam, 'viewingTeamId:', viewingTeamId);
    
    try {
      const marketplaceRef = collection(db, marketplacePath);
      
      let q;
      if (isViewingOtherTeam && viewingTeamId) {
        // Admin is viewing another team - show that team's listings
        debugLog('Loading listings for viewed team:', viewingTeamId);
        q = query(
          marketplaceRef,
          where('teamId', '==', viewingTeamId),
          orderBy('createdAt', 'desc')
        );
      } else {
        // Show current user's own listings
        q = query(
          marketplaceRef,
          where('sellerId', '==', user.uid),
          orderBy('createdAt', 'desc')
        );
      }
      
      const snapshot = await getDocs(q);
      setMyListings(snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })));
    } catch (err) {
      // If index not ready, retry without sorting
      if (err?.code === 'failed-precondition' || err?.message?.includes('index')) {
        console.warn('MyListings index not ready, fetching without sort order...');
        try {
          const marketplaceRef = collection(db, marketplacePath);
          
          const fallbackQuery = (isViewingOtherTeam && viewingTeamId)
            ? query(marketplaceRef, where('teamId', '==', viewingTeamId))
            : query(marketplaceRef, where('sellerId', '==', user.uid));
          
          const fallbackSnapshot = await getDocs(fallbackQuery);
          setMyListings(fallbackSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          })));
          setMyListingsLoading(false);
          return;
        } catch (fallbackErr) {
          console.error('Fallback query also failed:', fallbackErr);
        }
      }
      
      logFirebaseError('loadMyListings', err);
      // Silently fail - seller dashboard will show empty state
      setMyListings([]);
    } finally {
      setMyListingsLoading(false);
    }
  }, [hasMarketplaceAccess, user?.uid, marketplacePath, isViewingOtherTeam, viewingTeamId]);

  // Load user's purchases - or team's purchases when admin views another team
  const loadMyPurchases = useCallback(async () => {
    if (!hasMarketplaceAccess || !user?.uid) {
      setMyPurchases([]);
      setPurchasesLoading(false);
      return;
    }
    
    setPurchasesLoading(true);
    debugLog('loadMyPurchases - uid:', user.uid, 'path:', purchasesPath, 'isViewingOtherTeam:', isViewingOtherTeam);
    
    try {
      const purchasesRef = collection(db, purchasesPath);
      
      let q;
      if (isViewingOtherTeam && viewingTeamId) {
        // Admin is viewing another team - show that team's purchases
        debugLog('Loading purchases for viewed team:', viewingTeamId);
        q = query(
          purchasesRef,
          where('buyerTeamId', '==', viewingTeamId),
          where('status', '==', 'completed'),
          orderBy('purchasedAt', 'desc')
        );
      } else {
        // Show current user's own purchases
        q = query(
          purchasesRef,
          where('buyerId', '==', user.uid),
          where('status', '==', 'completed'),
          orderBy('purchasedAt', 'desc')
        );
      }
      
      // Use getDocsFromServer to bypass stale Firestore client cache
      let snapshot;
      try {
        snapshot = await getDocsFromServer(q);
      } catch (serverErr) {
        console.warn('Server fetch failed for purchases, trying cache:', serverErr.message);
        snapshot = await getDocs(q);
      }
      setMyPurchases(snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })));
    } catch (err) {
      // If index not ready, retry without sorting
      if (err?.code === 'failed-precondition' || err?.message?.includes('index')) {
        console.warn('MyPurchases index not ready, fetching without sort order...');
        try {
          const purchasesRef = collection(db, purchasesPath);
          
          const queryField = (isViewingOtherTeam && viewingTeamId) ? 'buyerTeamId' : 'buyerId';
          const queryValue = (isViewingOtherTeam && viewingTeamId) ? viewingTeamId : user.uid;
          debugLog(`Purchases fallback query: ${queryField}==${queryValue} in ${purchasesPath}`);
          
          const fallbackQuery = (isViewingOtherTeam && viewingTeamId)
            ? query(purchasesRef, where('buyerTeamId', '==', viewingTeamId))
            : query(purchasesRef, where('buyerId', '==', user.uid));
          
          let fallbackSnapshot;
          try {
            fallbackSnapshot = await getDocsFromServer(fallbackQuery);
            debugLog(`Purchases fallback (server): ${fallbackSnapshot.docs.length} docs found`);
          } catch (fbServerErr) {
            console.warn('Fallback server fetch failed, trying cache:', fbServerErr.message);
            fallbackSnapshot = await getDocs(fallbackQuery);
            debugLog(`Purchases fallback (cache): ${fallbackSnapshot.docs.length} docs found`);
          }
          // Filter for completed status client-side
          const allPurchases = fallbackSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
          const purchases = allPurchases.filter(p => p.status === 'completed');
          debugLog(`Purchases: ${allPurchases.length} total, ${purchases.length} completed`);
          
          setMyPurchases(purchases);
          setPurchasesLoading(false);
          return;
        } catch (fallbackErr) {
          console.error('Fallback query also failed:', fallbackErr);
        }
      }
      
      logFirebaseError('loadMyPurchases', err);
      // Silently fail - purchases page will show empty state
      setMyPurchases([]);
    } finally {
      setPurchasesLoading(false);
    }
  }, [hasMarketplaceAccess, user?.uid, purchasesPath, isViewingOtherTeam, viewingTeamId]);

  // Load pending review listings (Admin only)
  const loadPendingReviewListings = useCallback(async () => {
    if (!isAdmin) {
      setPendingReviewListings([]);
      return;
    }
    
    try {
      const marketplaceRef = collection(db, marketplacePath);
      const q = query(
        marketplaceRef,
        where('status', '==', LISTING_STATUS.PENDING_REVIEW),
        orderBy('createdAt', 'desc')
      );
      
      const snapshot = await getDocs(q);
      setPendingReviewListings(snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })));
    } catch (err) {
      // If index not ready, retry without sorting
      if (err?.code === 'failed-precondition' || err?.message?.includes('index')) {
        console.warn('PendingReviewListings index not ready, fetching without sort...');
        try {
          const marketplaceRef = collection(db, marketplacePath);
          const fallbackQuery = query(
            marketplaceRef,
            where('status', '==', LISTING_STATUS.PENDING_REVIEW)
          );
          
          const fallbackSnapshot = await getDocs(fallbackQuery);
          setPendingReviewListings(fallbackSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          })));
          return;
        } catch (fallbackErr) {
          console.error('Fallback query also failed:', fallbackErr);
        }
      }
      
      logFirebaseError('loadPendingReviewListings', err);
      // Silently fail - admin queue will show empty state  
      setPendingReviewListings([]);
    }
  }, [isAdmin, marketplacePath]);

  // Load reviewed listings (approved + rejected + deleted) for admin history
  const loadReviewedListings = useCallback(async () => {
    if (!isAdmin) {
      setReviewedListings([]);
      return;
    }

    try {
      const marketplaceRef = collection(db, marketplacePath);
      // Use a simple `in` query without orderBy to avoid composite index requirements.
      // Sort client-side after fetching.
      const q = query(
        marketplaceRef,
        where('status', 'in', [LISTING_STATUS.ACTIVE, LISTING_STATUS.REJECTED, LISTING_STATUS.DELETED]),
        limit(100)
      );

      const snapshot = await getDocs(q);
      const items = snapshot.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(item => item.reviewedBy || item.deletedBy)
        .sort((a, b) => {
          const aTime = a.deletedAt?.seconds || a.reviewedAt?.seconds || a.createdAt?.seconds || 0;
          const bTime = b.deletedAt?.seconds || b.reviewedAt?.seconds || b.createdAt?.seconds || 0;
          return bTime - aTime;
        });
      setReviewedListings(items);
    } catch (err) {
      logFirebaseError('loadReviewedListings', err);
      setReviewedListings([]);
    }
  }, [isAdmin, marketplacePath]);

  // Get a single listing by ID
  const getListingById = useCallback(async (listingId) => {
    if (!hasMarketplaceAccess) return null;
    
    try {
      const docRef = doc(db, marketplacePath, listingId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        // Increment view count
        await updateDoc(docRef, {
          viewCount: increment(1)
        });
        
        return {
          id: docSnap.id,
          ...docSnap.data()
        };
      }
      return null;
    } catch (err) {
      logFirebaseError('getListingById', err);
      return null;
    }
  }, [hasMarketplaceAccess, marketplacePath]);

  // Create a new listing
  const createListing = useCallback(async (templateId, listingData, template) => {
    if (!hasMarketplaceAccess || !user?.uid) {
      throw new Error('You do not have permission to create listings');
    }
    
    // Run validation checks
    const validationErrors = validateListing(listingData, template);
    const passedValidation = validationErrors.length === 0;
    
    // All new listings start as pending_review — admin must approve before going live.
    // Automated checks provide context for the reviewer (failedChecks field) but don't auto-approve.
    const status = LISTING_STATUS.PENDING_REVIEW;
    
    // Normalize graph: handle both {nodes: {...}} and flat formats
    const rawGraph = template?.graph;
    const graphNodes = rawGraph?.nodes && typeof rawGraph.nodes === 'object'
      ? rawGraph.nodes
      : rawGraph;
    
    // Calculate node count and check for coaching tips
    const nodeCount = graphNodes ? Object.keys(graphNodes).filter(k => k !== 'ui' && k !== 'nodes').length : 0;
    const hasCoachingTips = graphNodes
      ? Object.values(graphNodes).some(node => node?.coaching || node?.coachingNotes)
      : false;
    
    // Generate preview nodes (first 3-5 nodes), filtering metadata keys
    const previewNodes = graphNodes
      ? Object.entries(graphNodes)
          .filter(([k]) => k !== 'ui' && k !== 'nodes')
          .slice(0, 5)
          .map(([key, node]) => ({
            key,
            question: (node?.script || node?.question || '').substring(0, 100) || null,
            responseCount: node?.responses
              ? (Array.isArray(node.responses) ? node.responses.length : Object.keys(node.responses).length)
              : 0
          }))
      : [];
    
    const newListing = {
      templateId,
      sellerId: user.uid,
      sellerEmail: userEmail,
      sellerDisplayName: user.displayName || userEmail?.split('@')[0] || 'Anonymous',
      teamId: userTeam,
      title: listingData.title,
      description: listingData.description,
      category: listingData.category || 'other',
      tags: listingData.tags || [],
      price: listingData.isFree ? 0 : listingData.price,
      isFree: listingData.isFree || false,
      totalSales: 0,
      rating: 0,
      reviewCount: 0,
      viewCount: 0,
      nodeCount,
      hasCoachingTips,
      previewNodes,
      status,
      featured: false,
      createdAt: serverTimestamp(),
      publishedAt: null, // Set by admin on approval
      // QC fields
      reportCount: 0,
      failedChecks: validationErrors.map(e => e.check), // Info for admin reviewer
      rejectionReason: null,
      lastReviewedAt: null,
      reviewedBy: null
    };
    
    try {
      const marketplaceRef = collection(db, marketplacePath);
      const docRef = await addDoc(marketplaceRef, newListing);
      
      // Refresh my listings
      await loadMyListings();
      
      return {
        success: true,
        listingId: docRef.id,
        status,
        passedValidation,
        validationErrors
      };
    } catch (err) {
      logFirebaseError('createListing', err);
      throw err;
    }
  }, [hasMarketplaceAccess, user, userEmail, userTeam, validateListing, marketplacePath, loadMyListings]);

  // Update a listing
  const updateListing = useCallback(async (listingId, updates) => {
    if (!hasMarketplaceAccess || !user?.uid) {
      throw new Error('You do not have permission to update listings');
    }
    
    try {
      const docRef = doc(db, marketplacePath, listingId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        throw new Error('Listing not found');
      }
      
      // Check ownership (unless admin)
      if (docSnap.data().sellerId !== user.uid && !isAdmin) {
        throw new Error('You can only edit your own listings');
      }
      
      await updateDoc(docRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
      
      await loadMyListings();
      
      return { success: true };
    } catch (err) {
      logFirebaseError('updateListing', err);
      throw err;
    }
  }, [hasMarketplaceAccess, user?.uid, isAdmin, marketplacePath, loadMyListings]);

  // Delete a listing
  const deleteListing = useCallback(async (listingId) => {
    if (!hasMarketplaceAccess || !user?.uid) {
      throw new Error('You do not have permission to delete listings');
    }
    
    try {
      const docRef = doc(db, marketplacePath, listingId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        throw new Error('Listing not found');
      }
      
      // Check ownership (unless admin)
      if (docSnap.data().sellerId !== user.uid && !isAdmin) {
        throw new Error('You can only delete your own listings');
      }

      if (isAdmin) {
        // Admins soft-delete to preserve audit history in Past Reviews
        await updateDoc(docRef, {
          status: LISTING_STATUS.DELETED,
          deletedAt: serverTimestamp(),
          deletedBy: (() => {
            const displayName = user?.displayName || userEmail?.split('@')[0] || 'Admin';
            const team = userTeamName || 'Admin';
            return `${displayName} (${team})`;
          })()
        });
      } else {
        await deleteDoc(docRef);
      }
      await loadMyListings();
      
      return { success: true };
    } catch (err) {
      logFirebaseError('deleteListing', err);
      throw err;
    }
  }, [hasMarketplaceAccess, user, userEmail, userTeamName, isAdmin, marketplacePath, loadMyListings]);

  // Report a listing
  const reportListing = useCallback(async (listingId, reason, details = '') => {
    if (!hasMarketplaceAccess || !user?.uid) {
      throw new Error('You do not have permission to report listings');
    }
    
    try {
      const reportsRef = collection(db, reportsPath);
      // Create report
      await addDoc(reportsRef, {
        listingId,
        reporterId: user.uid,
        reporterEmail: userEmail,
        reason,
        details,
        status: 'pending',
        createdAt: serverTimestamp(),
        reviewedAt: null,
        reviewedBy: null
      });
      
      // Increment report count on listing
      const listingRef = doc(db, marketplacePath, listingId);
      const listingSnap = await getDoc(listingRef);
      
      if (listingSnap.exists()) {
        const newReportCount = (listingSnap.data().reportCount || 0) + 1;
        
        // Auto-hide if threshold reached (3+ reports)
        const updates = {
          reportCount: newReportCount
        };
        
        if (newReportCount >= 3 && listingSnap.data().status === LISTING_STATUS.ACTIVE) {
          updates.status = LISTING_STATUS.PENDING_REVIEW;
          updates.failedChecks = [...(listingSnap.data().failedChecks || []), 'community_reports'];
        }
        
        await updateDoc(listingRef, updates);
      }
      
      return { success: true };
    } catch (err) {
      logFirebaseError('reportListing', err);
      throw err;
    }
  }, [hasMarketplaceAccess, user?.uid, userEmail, reportsPath, marketplacePath]);

  // Admin: Approve a listing
  const approveListing = useCallback(async (listingId) => {
    if (!isAdmin) {
      throw new Error('Only admins can approve listings');
    }
    
    try {
      const docRef = doc(db, marketplacePath, listingId);
      await updateDoc(docRef, {
        status: LISTING_STATUS.ACTIVE,
        publishedAt: serverTimestamp(),
        reviewedAt: serverTimestamp(),
        reviewedBy: (() => {
          const displayName = user?.displayName || userEmail?.split('@')[0] || 'Admin';
          const team = userTeamName || 'Admin';
          return `${displayName} (${team})`;
        })(),
        failedChecks: [],
        reportCount: 0
      });
      
      await loadPendingReviewListings();
      
      return { success: true };
    } catch (err) {
      logFirebaseError('approveListing', err);
      throw err;
    }
  }, [isAdmin, marketplacePath, user, userEmail, userTeamName, loadPendingReviewListings]);

  // Admin: Reject a listing
  const rejectListing = useCallback(async (listingId, reason) => {
    if (!isAdmin) {
      throw new Error('Only admins can reject listings');
    }
    
    try {
      const docRef = doc(db, marketplacePath, listingId);
      await updateDoc(docRef, {
        status: LISTING_STATUS.REJECTED,
        rejectionReason: reason,
        reviewedAt: serverTimestamp(),
        reviewedBy: (() => {
          const displayName = user?.displayName || userEmail?.split('@')[0] || 'Admin';
          const team = userTeamName || 'Admin';
          return `${displayName} (${team})`;
        })()
      });
      
      await loadPendingReviewListings();
      
      // TODO: Send notification to seller
      
      return { success: true };
    } catch (err) {
      logFirebaseError('rejectListing', err);
      throw err;
    }
  }, [isAdmin, marketplacePath, userEmail, loadPendingReviewListings]);

  // Admin: Suspend a listing
  const suspendListing = useCallback(async (listingId, reason) => {
    if (!isAdmin) {
      throw new Error('Only admins can suspend listings');
    }
    
    try {
      const docRef = doc(db, marketplacePath, listingId);
      await updateDoc(docRef, {
        status: LISTING_STATUS.SUSPENDED,
        rejectionReason: reason,
        lastReviewedAt: serverTimestamp(),
        reviewedBy: userEmail
      });
      
      return { success: true };
    } catch (err) {
      logFirebaseError('suspendListing', err);
      throw err;
    }
  }, [isAdmin, marketplacePath, user, userEmail, userTeamName]);

  // Get reviews for a listing
  const getListingReviews = useCallback(async (listingId) => {
    if (!hasMarketplaceAccess) return [];
    
    try {
      const reviewsRef = collection(db, reviewsPath);
      const q = query(
        reviewsRef,
        where('listingId', '==', listingId),
        where('status', '==', 'published'),
        orderBy('createdAt', 'desc')
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (err) {
      // If index not ready, retry without sorting
      if (err?.code === 'failed-precondition' || err?.message?.includes('index')) {
        console.warn('ListingReviews index not ready, fetching without sort...');
        try {
          const reviewsRef = collection(db, reviewsPath);
          const fallbackQuery = query(
            reviewsRef,
            where('listingId', '==', listingId)
          );
          
          const fallbackSnapshot = await getDocs(fallbackQuery);
          // Filter for published status client-side
          return fallbackSnapshot.docs
            .map(doc => ({ id: doc.id, ...doc.data() }))
            .filter(r => r.status === 'published');
        } catch (fallbackErr) {
          console.error('Fallback query also failed:', fallbackErr);
        }
      }
      
      logFirebaseError('getListingReviews', err);
      return [];
    }
  }, [hasMarketplaceAccess, reviewsPath]);

  // Submit a review
  const submitReview = useCallback(async (listingId, purchaseId, reviewData) => {
    if (!hasMarketplaceAccess || !user?.uid) {
      throw new Error('You do not have permission to submit reviews');
    }
    
    try {
      // Verify purchase exists and belongs to user
      const purchaseRef = doc(db, purchasesPath, purchaseId);
      const purchaseSnap = await getDoc(purchaseRef);
      
      if (!purchaseSnap.exists() || purchaseSnap.data().buyerId !== user.uid) {
        throw new Error('You can only review scripts you have purchased');
      }
      
      const reviewsRef = collection(db, reviewsPath);
      // Create review
      await addDoc(reviewsRef, {
        listingId,
        purchaseId,
        reviewerId: user.uid,
        reviewerDisplayName: user.displayName || userEmail?.split('@')[0] || 'Anonymous',
        rating: reviewData.rating,
        title: reviewData.title?.trim() || null,
        body: reviewData.body?.trim() || null,
        verifiedPurchase: true,
        status: 'published',
        createdAt: serverTimestamp()
      });
      
      // Update listing rating (simple average for now)
      const listingRef = doc(db, marketplacePath, listingId);
      const listingSnap = await getDoc(listingRef);
      
      if (listingSnap.exists()) {
        const currentRating = listingSnap.data().rating || 0;
        const currentCount = listingSnap.data().reviewCount || 0;
        const newCount = currentCount + 1;
        const newRating = ((currentRating * currentCount) + reviewData.rating) / newCount;
        
        await updateDoc(listingRef, {
          rating: newRating,
          reviewCount: newCount
        });
      }
      
      return { success: true };
    } catch (err) {
      logFirebaseError('submitReview', err);
      throw err;
    }
  }, [hasMarketplaceAccess, user, userEmail, purchasesPath, reviewsPath, marketplacePath]);

  // Delete a review (Admin only)
  const deleteReview = useCallback(async (reviewId, listingId) => {
    if (!isAdmin) {
      throw new Error('Only admins can delete reviews');
    }

    try {
      const reviewRef = doc(db, reviewsPath, reviewId);
      const reviewSnap = await getDoc(reviewRef);

      if (!reviewSnap.exists()) {
        throw new Error('Review not found');
      }

      const reviewData = reviewSnap.data();
      const reviewRating = reviewData.rating || 0;
      const targetListingId = listingId || reviewData.listingId;

      await deleteDoc(reviewRef);

      // Recalculate listing rating with this review removed
      if (targetListingId) {
        const listingRef = doc(db, marketplacePath, targetListingId);
        const listingSnap = await getDoc(listingRef);

        if (listingSnap.exists()) {
          const currentRating = listingSnap.data().rating || 0;
          const currentCount = listingSnap.data().reviewCount || 0;
          const newCount = Math.max(0, currentCount - 1);
          const newRating = newCount > 0
            ? ((currentRating * currentCount) - reviewRating) / newCount
            : 0;

          await updateDoc(listingRef, {
            rating: newRating,
            reviewCount: newCount
          });
        }
      }

      return { success: true };
    } catch (err) {
      logFirebaseError('deleteReview', err);
      throw err;
    }
  }, [isAdmin, reviewsPath, marketplacePath]);

  // Check if user has purchased a listing
  const hasPurchased = useCallback((listingId) => {
    return myPurchases.some(p => p.listingId === listingId);
  }, [myPurchases]);

  // Check if user owns a listing
  const isOwner = useCallback((listing) => {
    return listing?.sellerId === user?.uid;
  }, [user?.uid]);

  // Search listings
  const searchListings = useCallback(async (searchTerm) => {
    if (!hasMarketplaceAccess || !searchTerm.trim()) return [];
    
    // Note: Firestore doesn't support full-text search natively
    // For production, consider Algolia or ElasticSearch
    // This is a basic implementation that searches by title prefix
    try {
      const marketplaceRef = collection(db, marketplacePath);
      const q = query(
        marketplaceRef,
        where('status', '==', LISTING_STATUS.ACTIVE),
        orderBy('title'),
        limit(20)
      );
      
      const snapshot = await getDocs(q);
      const term = searchTerm.toLowerCase();
      
      return snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(listing => 
          listing.title.toLowerCase().includes(term) ||
          listing.description.toLowerCase().includes(term) ||
          listing.tags?.some(tag => tag.toLowerCase().includes(term))
        );
    } catch (err) {
      logFirebaseError('searchListings', err);
      return [];
    }
  }, [hasMarketplaceAccess, marketplacePath]);

  // Track if data has been loaded (for lazy loading)
  const dataLoadedRef = useRef(false);
  
  // Initialize function - called by marketplace components when they mount
  const initializeMarketplace = useCallback(async () => {
    // Only load once per session
    if (dataLoadedRef.current) {
      debugLog('Marketplace already initialized, skipping');
      return;
    }
    
    if (!hasMarketplaceAccess) {
      debugLog('No marketplace access, skipping initialization');
      setLoading(false);
      setListingsLoading(false);
      setPurchasesLoading(false);
      setMyListingsLoading(false);
      return;
    }
    
    debugLog('Initializing marketplace data (lazy load)...');
    dataLoadedRef.current = true;
    
    try {
      await Promise.all([
        loadListings(),
        loadFeaturedListings(),
        loadMyListings(),
        loadMyPurchases(),
        isAdmin ? loadPendingReviewListings() : Promise.resolve()
      ]);
    } catch (err) {
      debugLog('Error in marketplace initialization:', err);
      setLoading(false);
    }
  }, [hasMarketplaceAccess, isAdmin, loadListings, loadFeaturedListings, loadMyListings, loadMyPurchases, loadPendingReviewListings]);

  // Reset data loaded flag when user changes (logout/login)
  useEffect(() => {
    if (!user?.uid) {
      dataLoadedRef.current = false;
      // Reset all states on logout
      setLoading(false);
      setListingsLoading(false);
      setPurchasesLoading(false);
      setMyListingsLoading(false);
      setListings([]);
      setFeaturedListings([]);
      setMyListings([]);
      setMyPurchases([]);
      setPendingReviewListings([]);
      setReviewedListings([]);
      setError(null);
    }
  }, [user?.uid]);

  // Reload team-specific data when admin switches viewing team
  useEffect(() => {
    if (hasMarketplaceAccess && user?.uid) {
      debugLog('Viewing team context changed, reloading marketplace data. isViewingOtherTeam:', isViewingOtherTeam, 'viewingTeamId:', viewingTeamId);
      loadMyListings();
      loadMyPurchases();
    }
  }, [viewingTeamId, isViewingOtherTeam]); // Only depend on viewing team state, not the functions

  // Set initial loading states to false (data will be loaded lazily)
  useEffect(() => {
    if (!hasMarketplaceAccess) {
      setLoading(false);
      setListingsLoading(false);
      setPurchasesLoading(false);
      setMyListingsLoading(false);
    }
  }, [hasMarketplaceAccess]);

  const value = useMemo(() => ({
    // State
    listings,
    featuredListings,
    myListings,
    myPurchases,
    pendingReviewListings,
    reviewedListings,
    loading,
    listingsLoading,
    purchasesLoading,
    myListingsLoading,
    error,
    hasMore,
    
    // Access control
    hasMarketplaceAccess,
    
    // Constants
    categories: MARKETPLACE_CATEGORIES,
    reportReasons: REPORT_REASONS,
    listingStatuses: LISTING_STATUS,
    pricingRules: PRICING_RULES,
    
    // Functions
    initializeMarketplace,
    loadListings,
    loadFeaturedListings,
    loadMyListings,
    loadMyPurchases,
    loadPendingReviewListings,
    loadReviewedListings,
    getListingById,
    createListing,
    updateListing,
    deleteListing,
    reportListing,
    approveListing,
    rejectListing,
    suspendListing,
    getListingReviews,
    submitReview,
    deleteReview,
    searchListings,
    validateListing,
    
    // Helpers
    hasPurchased,
    isOwner
  }), [
    listings, featuredListings, myListings, myPurchases, pendingReviewListings, reviewedListings,
    loading, listingsLoading, purchasesLoading, myListingsLoading, error, hasMore,
    hasMarketplaceAccess,
    initializeMarketplace, loadListings, loadFeaturedListings, loadMyListings, loadMyPurchases,
    loadPendingReviewListings, loadReviewedListings, getListingById, createListing, updateListing,
    deleteListing, reportListing, approveListing, rejectListing, suspendListing,
    getListingReviews, submitReview, deleteReview, searchListings, validateListing,
    hasPurchased, isOwner
  ]);

  return (
    <MarketplaceContext.Provider value={value}>
      {children}
    </MarketplaceContext.Provider>
  );
};

export default MarketplaceContext;
